import React from 'react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './MenuGrid.css';

export interface MenuGridItem {
  id: string;
  name: string;
  price: number;
  description?: string;
  currency?: string;
  available?: boolean;
  categoryId?: string;
  badge?: React.ReactNode;
}

export interface MenuGridCategory {
  id: string;
  label: React.ReactNode;
  count?: number;
}

export interface MenuGridProps {
  /** All items. When `categories` is provided, items are filtered by the selected tab via `categoryId`. */
  items: MenuGridItem[];
  /** Optional category tabs. Rendered if more than one category is supplied. */
  categories?: MenuGridCategory[];
  /** Controlled selected category id. If omitted, MenuGrid manages its own selection. */
  selectedCategoryId?: string;
  onCategoryChange?: (id: string) => void;
  /** Fires when a tile is clicked. */
  onItemClick?: (item: MenuGridItem) => void;
  /** Enables drag-to-reorder within the active category. */
  sortable?: boolean;
  onReorder?: (orderedItems: MenuGridItem[], categoryId?: string) => void;
  /** Denser tiles suitable for POS layouts on tablet. */
  dense?: boolean;
  emptyState?: React.ReactNode;
  className?: string;
}

export const MenuGrid: React.FC<MenuGridProps> = ({
  items,
  categories,
  selectedCategoryId,
  onCategoryChange,
  onItemClick,
  sortable,
  onReorder,
  dense,
  emptyState,
  className
}) => {
  const hasCategories = !!categories && categories.length > 1;
  const [internalCat, setInternalCat] = React.useState<string | undefined>(
    selectedCategoryId ?? categories?.[0]?.id
  );
  const activeCat = selectedCategoryId ?? internalCat;

  const filtered = React.useMemo(() => {
    if (!hasCategories || !activeCat) return items;
    return items.filter((it) => it.categoryId === activeCat);
  }, [items, hasCategories, activeCat]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const selectCategory = (id: string) => {
    setInternalCat(id);
    onCategoryChange?.(id);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const ids = filtered.map((it) => it.id);
    const oldIndex = ids.indexOf(String(e.active.id));
    const newIndex = ids.indexOf(String(e.over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const reorderedIds = arrayMove(ids, oldIndex, newIndex);
    const idToItem = new Map(items.map((it) => [it.id, it]));
    const reorderedActive = reorderedIds.map((id) => idToItem.get(id)!).filter(Boolean);
    if (!hasCategories) {
      onReorder?.(reorderedActive, activeCat);
      return;
    }
    // Merge back: keep other-category items where they are, replace active.
    const rebuilt = items.filter((it) => it.categoryId !== activeCat).concat(reorderedActive);
    onReorder?.(rebuilt, activeCat);
  };

  const classes = ['bso-menu-grid', dense ? 'bso-menu-grid--dense' : '', className ?? ''].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {hasCategories && (
        <div className="bso-menu-grid__tabs" role="tablist" aria-label="Categories">
          {categories!.map((cat) => {
            const count = cat.count ?? items.filter((it) => it.categoryId === cat.id).length;
            const active = cat.id === activeCat;
            return (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`bso-menu-grid__tab${active ? ' bso-menu-grid__tab--active' : ''}`}
                onClick={() => selectCategory(cat.id)}
              >
                {cat.label}
                <span className="bso-menu-grid__tab-count">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bso-menu-grid__empty">
          {emptyState ?? 'No items in this category.'}
        </div>
      ) : sortable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filtered.map((it) => it.id)} strategy={rectSortingStrategy}>
            <div className="bso-menu-grid__items">
              {filtered.map((it) => (
                <SortableTile key={it.id} item={it} onItemClick={onItemClick} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="bso-menu-grid__items">
          {filtered.map((it) => (
            <StaticTile key={it.id} item={it} onItemClick={onItemClick} />
          ))}
        </div>
      )}
    </div>
  );
};

const StaticTile: React.FC<{ item: MenuGridItem; onItemClick?: (i: MenuGridItem) => void }> = ({ item, onItemClick }) => (
  <button
    type="button"
    className={`bso-menu-grid__tile${item.available === false ? ' bso-menu-grid__tile--unavailable' : ''}`}
    onClick={() => onItemClick?.(item)}
  >
    <TileInner item={item} />
  </button>
);

const SortableTile: React.FC<{ item: MenuGridItem; onItemClick?: (i: MenuGridItem) => void }> = ({ item, onItemClick }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bso-menu-grid__tile${item.available === false ? ' bso-menu-grid__tile--unavailable' : ''}${isDragging ? ' bso-menu-grid__tile--dragging' : ''}`}
      onClick={() => !isDragging && onItemClick?.(item)}
      role="button"
      tabIndex={0}
    >
      <span
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className="bso-menu-grid__drag-handle"
        aria-label={`Reorder ${item.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <span aria-hidden="true">⋮⋮</span>
      </span>
      <TileInner item={item} />
    </div>
  );
};

const TileInner: React.FC<{ item: MenuGridItem }> = ({ item }) => (
  <>
    <div className="bso-menu-grid__tile-name">{item.name}</div>
    {item.description && <div className="bso-menu-grid__tile-desc">{item.description}</div>}
    {item.badge}
    <div className="bso-menu-grid__tile-price">
      {formatPrice(item.price, item.currency)}
    </div>
  </>
);

function formatPrice(price: number, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price);
  } catch {
    return `$${price.toFixed(2)}`;
  }
}
